import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOConfig, SEO_DEFAULTS, SEO_BASE_URL, truncateText, getOgImageForPath } from '@/config/seo';
import { SUPPORTED_LOCALES, LOCALE_CONFIGS, getLocaleFromPath, DEFAULT_LOCALE } from '@/i18n/types';

interface UniversalSEOProps {
  seo?: {
    title?: string;
    description?: string;
    canonical?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: "website" | "article";
    noindex?: boolean;
    indexable?: boolean;
  };
  structuredData?: Record<string, unknown>;
  breadcrumbLabels?: Record<string, string>;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const UniversalSEO: React.FC<UniversalSEOProps> = ({
  seo = {},
  structuredData,
  author,
  publishedTime,
  modifiedTime
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Detect current locale from path
  const currentLocale = getLocaleFromPath(currentPath);
  const localeConfig = currentLocale ? LOCALE_CONFIGS[currentLocale] : LOCALE_CONFIGS[DEFAULT_LOCALE];
  
  // Get SEO config with locale-aware titles/descriptions
  const configSEO = getSEOConfig(currentPath, currentLocale);
  
  const finalSEO = {
    title: truncateText(seo.title || configSEO.title || SEO_DEFAULTS.title, 60),
    description: truncateText(seo.description || configSEO.description || SEO_DEFAULTS.description, 155),
    canonical: seo.canonical || configSEO.canonical,
    keywords: seo.keywords || configSEO.keywords || SEO_DEFAULTS.keywords,
    ogImage: seo.ogImage || getOgImageForPath(currentPath) || SEO_DEFAULTS.ogImage,
    ogType: seo.ogType || configSEO.ogType || SEO_DEFAULTS.ogType,
    noindex: seo.noindex || configSEO.noindex || false,
    indexable: seo.indexable !== undefined ? seo.indexable : !(seo.noindex || configSEO.noindex || false)
  };

  const absoluteOgImage = finalSEO.ogImage.startsWith('http') 
    ? finalSEO.ogImage 
    : `${SEO_BASE_URL}${finalSEO.ogImage}`;

  // Canonical hardening: force https + non-www host, strip query/hash,
  // normalise trailing slash (only "/" keeps it).
  const rawCanonical = finalSEO.canonical.startsWith('http')
    ? finalSEO.canonical
    : `${SEO_BASE_URL}${finalSEO.canonical}`;
  const absoluteCanonical = (() => {
    try {
      const u = new URL(rawCanonical);
      u.protocol = 'https:';
      if (u.hostname === 'www.puntohispano.ch') u.hostname = 'puntohispano.ch';
      u.search = '';
      u.hash = '';
      let path = u.pathname.replace(/\/+$/, '') || '/';
      return `${u.origin}${path}`;
    } catch {
      return rawCanonical;
    }
  })();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalSEO.title}</title>
      <meta name="description" content={finalSEO.description} />
      <meta name="keywords" content={finalSEO.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={absoluteCanonical} />
      
      {/* Robots Meta */}
      <meta name="robots" content={!finalSEO.indexable ? "noindex,nofollow" : "index,follow"} />
      
      {/* Language and Locale */}
      <meta name="language" content={currentLocale} />
      <meta httpEquiv="content-language" content={localeConfig.hreflang} />
      
      {/* Open Graph */}
      <meta property="og:title" content={finalSEO.title} />
      <meta property="og:description" content={finalSEO.description} />
      <meta property="og:url" content={absoluteCanonical} />
      <meta property="og:type" content={finalSEO.ogType} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
      <meta property="og:locale" content={localeConfig.hreflang.replace('-', '_')} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalSEO.title} />
      <meta name="twitter:description" content={finalSEO.description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      
      {/* Article-specific meta (for blog posts) */}
      {finalSEO.ogType === 'article' && author && (
        <>
          <meta name="author" content={author} />
          <meta property="article:author" content={author} />
        </>
      )}
      
      {finalSEO.ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      
      {finalSEO.ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Business Information */}
      <meta name="geo.region" content="CH-ZH" />
      <meta name="geo.placename" content="Zürich" />
      <meta name="geo.position" content="47.4245;8.5692" />
      <meta name="ICBM" content="47.4245, 8.5692" />
      
      {/* Additional structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Performance and Preconnect */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      
      {/* Preload critical images */}
      <link rel="preload" as="image" href={absoluteOgImage} />
      
      {/* Hreflang for multilingual SEO — strip query/hash + normalise trailing slash */}
      {(() => {
        const cleanPath = currentPath
          .replace(/^\/(es|de|fr|it|pt|en)(\/|$)/, '/')
          .replace(/\/+$/, '') || '/';
        return (
          <>
            {SUPPORTED_LOCALES.map((loc) => {
              const localizedUrl = loc === DEFAULT_LOCALE
                ? `${SEO_BASE_URL}${cleanPath}`
                : `${SEO_BASE_URL}/${loc}${cleanPath === '/' ? '' : cleanPath}`;
              return (
                <link key={loc} rel="alternate" hrefLang={LOCALE_CONFIGS[loc].hreflang} href={localizedUrl} />
              );
            })}
            {/* x-default points to ES (default market) per Google guidance */}
            <link rel="alternate" hrefLang="x-default" href={`${SEO_BASE_URL}${cleanPath}`} />
          </>
        );
      })()}
      {/* Additional meta for better indexing */}
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      
      {/* Mobile optimization */}
      <meta name="theme-color" content="#A81C1C" />
      <meta name="msapplication-navbutton-color" content="#A81C1C" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
    </Helmet>
  );
};

export default UniversalSEO;